var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from '@emotion/styled';
import { CheckboxChecked, CheckboxUnchecked, Download } from '../../assets';
import Highcharts from 'highcharts';
import Text from '../Text';
import { renderToString } from 'react-dom/server';
import colors from '../../themes/colors';
import { InsightsMetric } from '../../types/chart';
var formatMonthDayYearDate = function (date) {
    // the replace is here because of JS Date math; using a '-' returns the previous date, using a '/' returns the passed in date
    var newDate = new Date(date.replaceAll('-', '/'));
    var monthYearFormatOptions = {
        month: 'short',
        day: '2-digit',
        year: '2-digit'
    };
    var monthDayYearFormatter = new Intl.DateTimeFormat('en-US', monthYearFormatOptions).format;
    var formattedDate = monthDayYearFormatter(newDate);
    var dateWithApostrophe = formattedDate.replace(', ', " '");
    return dateWithApostrophe;
};
var yAxisUnit = function (selectedMetric) {
    switch (selectedMetric) {
        case InsightsMetric.Cpa:
        case InsightsMetric.Cpc:
        case InsightsMetric.Cpm:
        case InsightsMetric.Spend:
            // eslint-disable-next-line
            return '${text}';
        case InsightsMetric.Ctr:
        case InsightsMetric.Cvr:
            // eslint-disable-next-line
            return '{text}%';
        default:
            // eslint-disable-next-line
            return '{text}';
    }
};
var addTooltipPrefix = function (selectedMetric) {
    switch (selectedMetric) {
        case InsightsMetric.Cpa:
        case InsightsMetric.Cpc:
        case InsightsMetric.Cpm:
        case InsightsMetric.Spend:
            return '$';
        default:
            return '';
    }
};
var addTooltipSuffix = function (selectedMetric) {
    switch (selectedMetric) {
        case InsightsMetric.Ctr:
        case InsightsMetric.Cvr:
            return '%';
        default:
            return '';
    }
};
/* These are the indicies for the rankings array we get from the API
  The "high" value is always the first object in the array (name: 1)
  The "average" value is always the second object in the array (name: 2)
  The "low" value is always the third object in the array (name: 3)
  If any of these are missing, then the values for that ranking are zeroes
*/
var InsightsIndex;
(function (InsightsIndex) {
    InsightsIndex[InsightsIndex["HighPerformers"] = 0] = "HighPerformers";
    InsightsIndex[InsightsIndex["Average"] = 1] = "Average";
    InsightsIndex[InsightsIndex["LowPerformers"] = 2] = "LowPerformers";
})(InsightsIndex || (InsightsIndex = {}));
var DownloadButton = function () { return (_jsx("div", __assign({ style: {
        backgroundColor: colors.neutral10,
        borderRadius: '4px',
        padding: '6px',
        width: '28px'
    } }, { children: _jsx(Download, {}) }))); };
var LegendCheckboxWrapper = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  background-color: ", ";\n  border-radius: 4px;\n  display: flex;\n  gap: 6px;\n  padding: 4px 6px;\n\n  & rect:first-of-type {\n    fill: ", ";\n  }\n\n  & rect:last-of-type {\n    stroke: ", ";\n  }\n\n  & path {\n    fill: white;\n    stroke: white;\n    transform-origin: 12px;\n  }\n\n  & span {\n    line-height: 22px;\n  }\n"], ["\n  background-color: ", ";\n  border-radius: 4px;\n  display: flex;\n  gap: 6px;\n  padding: 4px 6px;\n\n  & rect:first-of-type {\n    fill: ", ";\n  }\n\n  & rect:last-of-type {\n    stroke: ", ";\n  }\n\n  & path {\n    fill: white;\n    stroke: white;\n    transform-origin: 12px;\n  }\n\n  & span {\n    line-height: 22px;\n  }\n"])), colors.neutral5, function (_a) {
    var color = _a.color, isVisible = _a.isVisible;
    return (isVisible ? color : colors.neutral5);
}, function (_a) {
    var color = _a.color;
    return color;
});
var LegendCheckbox = function (_a) {
    var checkboxColor = _a.checkboxColor, isVisible = _a.isVisible, legendName = _a.legendName;
    return (_jsxs(LegendCheckboxWrapper, __assign({ color: checkboxColor, isVisible: isVisible }, { children: [isVisible ? _jsx(CheckboxChecked, {}) : _jsx(CheckboxUnchecked, {}), _jsx(Text, __assign({ variant: "body3" }, { children: legendName }))] }), legendName));
};
// All of the format options for Highcharts
var highchartsOptions = function (_a) {
    var brandInsights = _a.brandInsights, flavorCatInsights = _a.flavorCatInsights, selectedMetric = _a.selectedMetric;
    // This helps us display a date even if some data isn't available
    var flavorCatInsightDates = flavorCatInsights.map(function (flavorCatInsight) { return flavorCatInsight.date; });
    var brandInsightDates = brandInsights.map(function (brandInsight) { return brandInsight.date; });
    if (flavorCatInsights.length !== brandInsightDates.length)
        return {};
    var chartDates = flavorCatInsights.length !== 0 ? flavorCatInsightDates : brandInsightDates;
    return {
        chart: {
            borderRadius: 8,
            marginLeft: 50,
            marginTop: 50
        },
        credits: {
            enabled: false
        },
        exporting: {
            enabled: true,
            allowHTML: true,
            buttons: {
                contextButton: {
                    enabled: false
                },
                exportButton: {
                    text: renderToString(_jsx(DownloadButton, {})),
                    useHTML: true,
                    menuItems: [
                        'viewFullscreen',
                        'printChart',
                        'separator',
                        'downloadCSV',
                        'downloadPNG',
                        'downloadJPEG',
                        'downloadPDF',
                        'downloadSVG',
                    ]
                }
            },
            chartOptions: {
                legend: {
                    useHTML: false,
                    // eslint-disable-next-line
                    labelFormatter: function () {
                        return this.name;
                    },
                    symbolWidth: 20
                }
            },
            csv: {
                // @ts-ignore
                // eslint-disable-next-line
                columnHeaderFormatter: function (item) {
                    if (!item || item instanceof Highcharts.Axis) {
                        return 'Date';
                    }
                    return item.name;
                }
            }
        },
        legend: {
            enabled: true,
            useHTML: true,
            // eslint-disable-next-line
            labelFormatter: function () {
                return renderToString(_jsx(LegendCheckbox, { checkboxColor: this.color, legendName: this.name, isVisible: this.visible }));
            },
            symbolWidth: 0
        },
        navigation: {
            buttonOptions: {
                align: 'right',
                verticalAlign: 'top'
            }
        },
        series: [
            {
                name: 'Your Brand',
                data: brandInsights.map(function (brandInsight) { return (brandInsight === null || brandInsight === void 0 ? void 0 : brandInsight.value) || 0; }),
                type: 'line',
                color: colors.bigBangBlack,
                marker: {
                    fillColor: colors.bigBangBlack
                },
                selected: true,
                events: {
                    // eslint-disable-next-line
                    checkboxClick: function (event) {
                        if (event.checked) {
                            this.show();
                        }
                        else {
                            this.hide();
                        }
                    }
                }
            },
            {
                name: 'High Performers',
                data: flavorCatInsights.map(function (flavorCatInsight) { var _a; return ((_a = flavorCatInsight.rankings[InsightsIndex.HighPerformers]) === null || _a === void 0 ? void 0 : _a.value) || 0; }),
                type: 'line',
                color: colors.green30,
                dashStyle: 'Dash',
                marker: {
                    fillColor: colors.green30
                },
                selected: true,
                events: {
                    // eslint-disable-next-line
                    checkboxClick: function (event) {
                        if (event.checked) {
                            this.show();
                        }
                        else {
                            this.hide();
                        }
                    }
                }
            },
            {
                name: 'Average Performers',
                data: flavorCatInsights.map(function (flavorCatInsight) { var _a; return ((_a = flavorCatInsight.rankings[InsightsIndex.Average]) === null || _a === void 0 ? void 0 : _a.value) || 0; }),
                type: 'line',
                color: colors.yellow30,
                dashStyle: 'Dash',
                marker: {
                    fillColor: colors.yellow30
                },
                selected: true,
                events: {
                    // eslint-disable-next-line
                    checkboxClick: function (event) {
                        if (event.checked) {
                            this.show();
                        }
                        else {
                            this.hide();
                        }
                    }
                }
            },
        ],
        title: {
            text: undefined
        },
        tooltip: {
            valueDecimals: 2,
            valuePrefix: addTooltipPrefix(selectedMetric),
            valueSuffix: addTooltipSuffix(selectedMetric)
        },
        xAxis: {
            categories: chartDates,
            crosshair: true,
            labels: {
                // eslint-disable-next-line
                formatter: function () {
                    return formatMonthDayYearDate(this.value);
                }
            }
        },
        yAxis: {
            min: 0,
            minRange: 0.1,
            labels: {
                align: 'left',
                format: yAxisUnit(selectedMetric),
                x: -30,
                y: -2
            },
            title: {
                align: 'high',
                textAlign: 'left',
                margin: 5,
                offset: 0,
                rotation: 0,
                text: selectedMetric.toLocaleUpperCase(),
                x: -30,
                y: -20
            }
        }
    };
};
export default highchartsOptions;
var templateObject_1;
